import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './CompanyList.scss';

interface Company {
  id: number;
  name: string;
  status: 'pending' | 'approved' | 'rejected';
  cv_count: number;
  activity_sector?: string;
  created_at: string;
  approved_at?: string;
}

interface Stats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

const CompanyList: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const status = statusFilter !== 'all' ? statusFilter : undefined;
      const response = await api.get('/companies', { params: { status } });
      if (response.data.success) {
        setCompanies(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/companies/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
    fetchStats();
  }, [fetchCompanies, fetchStats]);

  const handleApprove = async (id: number) => {
    try {
      await api.post(`/companies/${id}/approve`);
      fetchCompanies();
      fetchStats();
    } catch (error) {
      console.error('Failed to approve company:', error);
    }
  };

  const handleReject = async (id: number) => {
    try {
      await api.post(`/companies/${id}/reject`);
      fetchCompanies();
      fetchStats();
    } catch (error) {
      console.error('Failed to reject company:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Ar tikrai norite ištrinti šią įmonę?')) return;
    try {
      await api.delete(`/companies/${id}`);
      fetchCompanies();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete company:', error);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    try {
      await api.post('/companies/bulk-approve', { ids: selectedIds });
      setSelectedIds([]);
      fetchCompanies();
      fetchStats();
    } catch (error) {
      console.error('Failed to bulk approve:', error);
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.length === 0) return;
    try {
      await api.post('/companies/bulk-reject', { ids: selectedIds });
      setSelectedIds([]);
      fetchCompanies();
      fetchStats();
    } catch (error) {
      console.error('Failed to bulk reject:', error);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await api.post('/companies/sync');
      if (response.data.success) {
        alert(`Sinchronizuota: ${response.data.data.added} pridėta, ${response.data.data.updated} atnaujinta`);
        fetchCompanies();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to sync companies:', error);
      alert('Nepavyko sinchronizuoti įmonių');
    } finally {
      setSyncing(false);
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredCompanies.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredCompanies.map(c => c.id));
    }
  };

  const filteredCompanies = companies.filter(company => 
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <span className="status-badge approved">Patvirtinta</span>;
      case 'rejected':
        return <span className="status-badge rejected">Atmesta</span>;
      default:
        return <span className="status-badge pending">Laukia</span>;
    }
  };

  return (
    <div className="company-list-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Įmonių Valdymas</h1>
          <p className="subtitle">Tvirtinkite ir valdykite įmones iš CV darbo patirties</p>
        </div>
        <button 
          className="sync-btn"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? '⏳ Sinchronizuojama...' : '🔄 Sinchronizuoti iš CV'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Viso įmonių</div>
        </div>
        <div className="stat-card pending">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Laukia patvirtinimo</div>
        </div>
        <div className="stat-card approved">
          <div className="stat-value">{stats.approved}</div>
          <div className="stat-label">Patvirtinta</div>
        </div>
        <div className="stat-card rejected">
          <div className="stat-value">{stats.rejected}</div>
          <div className="stat-label">Atmesta</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Ieškoti įmonės..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select 
          className="status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Visos</option>
          <option value="pending">Laukiančios</option>
          <option value="approved">Patvirtintos</option>
          <option value="rejected">Atmestos</option>
        </select>

        {selectedIds.length > 0 && (
          <div className="bulk-actions">
            <span className="selected-count">{selectedIds.length} pasirinkta</span>
            <button className="bulk-approve" onClick={handleBulkApprove}>
              ✓ Patvirtinti
            </button>
            <button className="bulk-reject" onClick={handleBulkReject}>
              ✗ Atmesti
            </button>
          </div>
        )}
      </div>

      {/* Companies Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading">Kraunama...</div>
        ) : filteredCompanies.length === 0 ? (
          <div className="empty-state">
            <p>Įmonių nerasta</p>
          </div>
        ) : (
          <table className="companies-table">
            <thead>
              <tr>
                <th>
                  <input 
                    type="checkbox"
                    checked={selectedIds.length === filteredCompanies.length && filteredCompanies.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>ID</th>
                <th>Įmonės pavadinimas</th>
                <th>CV skaičius</th>
                <th>Veiklos sritis</th>
                <th>Statusas</th>
                <th>Sukurta</th>
                <th>Veiksmai</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map(company => (
                <tr key={company.id} className={selectedIds.includes(company.id) ? 'selected' : ''}>
                  <td>
                    <input 
                      type="checkbox"
                      checked={selectedIds.includes(company.id)}
                      onChange={() => toggleSelect(company.id)}
                    />
                  </td>
                  <td>{company.id}</td>
                  <td className="company-name">{company.name}</td>
                  <td className="cv-count">{company.cv_count}</td>
                  <td className="sector">{company.activity_sector || '-'}</td>
                  <td>{getStatusBadge(company.status)}</td>
                  <td className="date">
                    {new Date(company.created_at).toLocaleDateString('lt-LT')}
                  </td>
                  <td className="actions">
                    {company.status === 'pending' && (
                      <>
                        <button 
                          className="action-btn approve"
                          onClick={() => handleApprove(company.id)}
                          title="Patvirtinti"
                        >
                          ✓
                        </button>
                        <button 
                          className="action-btn reject"
                          onClick={() => handleReject(company.id)}
                          title="Atmesti"
                        >
                          ✗
                        </button>
                      </>
                    )}
                    <button 
                      className="action-btn delete"
                      onClick={() => handleDelete(company.id)}
                      title="Ištrinti"
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CompanyList;

